// src/pages/incidents/IncidentList.js - VERSION AVEC GESTION ERREUR ABONNEMENT
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Layout from "../../components/Layout";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL ||
  "http://localhost/securecontrol-backend";

// Palette de couleurs SecureControl
const COLORS = {
  primary: "#0B3C5D", // Bleu Sécurité
  secondary: "#1E88C8", // Bleu Tech
  accent: "#4FC3F7", // Bleu Clair
  darkText: "#2E2E2E", // Gris Foncé
  lightBg: "#F5F7FA", // Gris Clair
  white: "#FFFFFF",
  danger: "#f44336",
  success: "#4caf50",
  warning: "#ff9800",
  info: "#2196f3",
};

const formatDate = (dateString) => {
  if (
    !dateString ||
    dateString === "0000-00-00" ||
    dateString === "0000-00-00 00:00:00"
  ) {
    return "-";
  }
  const date = new Date(dateString);
  return date.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

// Date locale du jour au format YYYY-MM-DD (pour défaut des filtres date) —
// new Date().toISOString() donne la date en UTC, ce qui décale le jour selon
// l'heure locale ; on reconstruit donc la date à partir des getters locaux.
const getTodayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const IncidentList = () => {
  const navigate = useNavigate();
  const [incidents, setIncidents] = useState([]);
  const [filteredIncidents, setFilteredIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [serverError, setServerError] = useState("");
  const [updatingId, setUpdatingId] = useState(null);
  const [infoMsg, setInfoMsg] = useState("");

  // États pour l'erreur d'abonnement
  const [abonnementError, setAbonnementError] = useState(false);
  const [abonnementMessage, setAbonnementMessage] = useState("");

  // États pour les filtres
  const [filterSite, setFilterSite] = useState("");
  const [filterStatut, setFilterStatut] = useState("");
  const [filterControleur, setFilterControleur] = useState("");
  // Par défaut, la journée en cours.
  const [filterDateDebut, setFilterDateDebut] = useState(getTodayISO());
  const [filterDateFin, setFilterDateFin] = useState(getTodayISO());

  // États pour la pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // États pour les données de filtrage
  const [sitesList, setSitesList] = useState([]);
  const [controleursList, setControleursList] = useState([]);

  // États pour le modal de confirmation
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedIncident, setSelectedIncident] = useState(null);
  const [actionResolutionText, setActionResolutionText] = useState("");
  const [resolveFieldError, setResolveFieldError] = useState("");

  // Fonction pour réinitialiser l'erreur d'abonnement
  const resetAbonnementError = () => {
    setAbonnementError(false);
    setAbonnementMessage("");
  };

  useEffect(() => {
    fetchIncidents();
  }, []);

  // Mettre à jour les listes de filtres
  useEffect(() => {
    if (incidents.length > 0) {
      const sites = [];
      const siteIds = new Set();

      incidents.forEach((incident) => {
        if (
          incident.site &&
          incident.site.id &&
          !siteIds.has(incident.site.id)
        ) {
          siteIds.add(incident.site.id);
          const siteName = getSiteName(incident.site);
          sites.push({
            id: incident.site.id,
            nom_complet: siteName,
            ...incident.site,
          });
        }
      });

      sites.sort((a, b) => a.nom_complet.localeCompare(b.nom_complet));
      setSitesList(sites);

      const controleurs = [];
      const controleurIds = new Set();

      incidents.forEach((incident) => {
        if (
          incident.controleur &&
          incident.controleur.id_user &&
          !controleurIds.has(incident.controleur.id_user)
        ) {
          controleurIds.add(incident.controleur.id_user);
          const controleurName = getControleurName(incident.controleur);
          controleurs.push({
            id: incident.controleur.id_user,
            nom_complet: controleurName,
            ...incident.controleur,
          });
        }
      });

      controleurs.sort((a, b) => a.nom_complet.localeCompare(b.nom_complet));
      setControleursList(controleurs);
    }
  }, [incidents]);

  // Appliquer les filtres
  useEffect(() => {
    let filtered = [...incidents];

    if (filterSite) {
      filtered = filtered.filter(
        (incident) =>
          incident.site &&
          incident.site.id &&
          parseInt(incident.site.id) === parseInt(filterSite),
      );
    }

    if (filterStatut) {
      filtered = filtered.filter((incident) => {
        if (filterStatut === "résolu") {
          return incident.date_resolus_incident !== null;
        } else if (filterStatut === "non résolu") {
          return incident.date_resolus_incident === null;
        }
        return true;
      });
    }

    if (filterControleur) {
      filtered = filtered.filter(
        (incident) =>
          incident.controleur &&
          incident.controleur.id_user &&
          parseInt(incident.controleur.id_user) === parseInt(filterControleur),
      );
    }

    // Filtrage par plage de dates
    if (filterDateDebut || filterDateFin) {
      filtered = filtered.filter((incident) => {
        const incidentDate = new Date(incident.date_creer_incident);
        incidentDate.setHours(0, 0, 0, 0);

        let isAfterStart = true;
        let isBeforeEnd = true;

        if (filterDateDebut) {
          const startDate = new Date(filterDateDebut);
          startDate.setHours(0, 0, 0, 0);
          isAfterStart = incidentDate >= startDate;
        }

        if (filterDateFin) {
          const endDate = new Date(filterDateFin);
          endDate.setHours(23, 59, 59, 999);
          isBeforeEnd = incidentDate <= endDate;
        }

        return isAfterStart && isBeforeEnd;
      });
    }

    setFilteredIncidents(filtered);
    setCurrentPage(1);

    if (filtered.length === 0) {
      setInfoMsg("Aucun incident ne correspond aux critères de recherche.");
    } else {
      setInfoMsg("");
    }
  }, [
    incidents,
    filterSite,
    filterStatut,
    filterControleur,
    filterDateDebut,
    filterDateFin,
  ]);

  const fetchIncidents = async () => {
    setLoading(true);
    setServerError("");
    setInfoMsg("");
    resetAbonnementError();

    try {
      const token = localStorage.getItem("access_token");
      if (!token) {
        setServerError("Session expirée. Veuillez vous reconnecter.");
        setTimeout(() => navigate("/login"), 2000);
        return;
      }

      const url = `${API_BASE_URL}/incidents/liste_incidents.php`;

      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      });

      if (response.data?.success) {
        setIncidents(response.data.data || []);

        if ((response.data.data || []).length === 0) {
          setInfoMsg("Aucun incident trouvé.");
        }
      } else {
        setServerError(
          response.data?.message ||
            "Impossible de charger la liste des incidents.",
        );
      }
    } catch (err) {
      // Vérifier si c'est une erreur 403 avec code ABONNEMENT_REQUIS
      if (err.response?.status === 403 && err.response?.data?.code === 'ABONNEMENT_REQUIS') {
        setAbonnementError(true);
        setAbonnementMessage(err.response.data.message || "Abonnement inactif");
        setIncidents([]);
      } else {
        handleFetchError(err);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFetchError = (err) => {
    if (err.response?.status === 401) {
      setServerError("Session expirée. Veuillez vous reconnecter.");
      localStorage.removeItem("access_token");
      setTimeout(() => navigate("/login"), 1000);
    } else if (err.response?.status === 403) {
      setServerError(
        "Accès interdit. Vous n'avez pas les permissions nécessaires.",
      );
    } else if (err.response?.status === 500) {
      setServerError("Erreur serveur. Veuillez contacter l'administrateur.");
    } else if (err.request) {
      setServerError("Serveur inaccessible. Vérifiez votre connexion.");
    } else {
      setServerError("Erreur technique lors du chargement des incidents.");
    }
  };

  const handleViewDetails = (incident) => {
    const incidentId = incident.id_incident;

    if (!incidentId) {
      alert("ID d'incident invalide");
      return;
    }
    navigate(`/incidents/${incidentId}`);
  };

  const openConfirmModal = (incident) => {
    setSelectedIncident(incident);
    setActionResolutionText("");
    setResolveFieldError("");
    setShowConfirmModal(true);
  };

  const closeConfirmModal = () => {
    setShowConfirmModal(false);
    setSelectedIncident(null);
    setActionResolutionText("");
    setResolveFieldError("");
  };

  const handleMarkAsResolved = async () => {
    if (!selectedIncident || !selectedIncident.id_incident) {
      alert("ID d'incident invalide");
      closeConfirmModal();
      return;
    }

    const texte = actionResolutionText.trim();
    if (!texte) {
      setResolveFieldError("Merci de décrire les actions effectuées pour résoudre cet incident.");
      return;
    }

    setResolveFieldError("");
    setUpdatingId(selectedIncident.id_incident);
    resetAbonnementError();

    try {
      const token = localStorage.getItem("access_token");
      if (!token) {
        alert("Session expirée. Veuillez vous reconnecter.");
        navigate("/login");
        closeConfirmModal();
        return;
      }

      const url = `${API_BASE_URL}/incidents/resolus_incidents.php`;

      const response = await axios.post(
        url,
        {
          id_incident: selectedIncident.id_incident,
          est_resolu: true,
          action_resolution: texte,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        },
      );

      if (response.data?.success) {
        fetchIncidents();
        alert(
          response.data.message || "Incident marqué comme résolu avec succès.",
        );
        setUpdatingId(null);
        closeConfirmModal();
      } else {
        setResolveFieldError(
          response.data?.message || "Erreur lors de la mise à jour du statut.",
        );
        setUpdatingId(null);
      }
    } catch (err) {
      console.error("Erreur:", err);

      // Vérifier si c'est une erreur 403 avec code ABONNEMENT_REQUIS
      if (err.response?.status === 403 && err.response?.data?.code === 'ABONNEMENT_REQUIS') {
        setAbonnementError(true);
        setAbonnementMessage(err.response.data.message || "Abonnement inactif");
        setResolveFieldError("Impossible de résoudre l'incident : abonnement inactif.");
      } else if (err.response?.data?.message) {
        setResolveFieldError(err.response.data.message);
      } else {
        setResolveFieldError("Erreur lors de la mise à jour du statut.");
      }
      setUpdatingId(null);
    }
  };

  const getAgentName = (agent) => {
    if (!agent) return "N/A";
    if (agent.nom_agent && agent.prenom_agent) {
      return `${agent.nom_agent} ${agent.prenom_agent}`;
    }
    if (agent.nom_agent) return agent.nom_agent;
    return "Agent";
  };

  const getSiteName = (site) => {
    if (!site) return "N/A";
    if (site.nom) return site.nom;
    if (site.nom_site) return site.nom_site;
    return "Site";
  };

  const getControleurName = (controleur) => {
    if (!controleur) return "N/A";
    if (controleur.prenom_user && controleur.nom_user) {
      return `${controleur.prenom_user} ${controleur.nom_user}`;
    }
    if (controleur.prenom && controleur.nom) {
      return `${controleur.prenom} ${controleur.nom}`;
    }
    return "N/A";
  };

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredIncidents.slice(
    indexOfFirstItem,
    indexOfLastItem,
  );
  const totalPages = Math.ceil(filteredIncidents.length / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(parseInt(e.target.value));
    setCurrentPage(1);
  };

  const resetFilters = () => {
    setFilterSite("");
    setFilterStatut("");
    setFilterControleur("");
    setFilterDateDebut("");
    setFilterDateFin("");
  };

  const hasActiveFilters = () => {
    return (
      filterSite ||
      filterStatut ||
      filterControleur ||
      filterDateDebut ||
      filterDateFin
    );
  };

  // Composant pour l'affichage d'aucun résultat
  const renderNoResults = () => (
    <div className="text-center py-5">
      <div className="mb-4">
        <div
          className="rounded-circle d-flex align-items-center justify-content-center mx-auto"
          style={{
            width: "80px",
            height: "80px",
            backgroundColor: "#F5F7FA",
            color: "#B0BEC5",
          }}
        >
          <i className="bi bi-search fs-1"></i>
        </div>
      </div>
      <h6 className="mb-3" style={{ color: COLORS.primary }}>
        Aucun résultat
      </h6>
      <p
        className="text-muted"
        style={{ color: COLORS.darkText, maxWidth: "500px", margin: "0 auto" }}
      >
        Aucun incident ne correspond aux critères de recherche.
      </p>
      <button
        className="btn fw-semibold mt-3"
        onClick={resetFilters}
        style={{
          color: COLORS.primary,
          borderColor: COLORS.primary,
          backgroundColor: "transparent",
          borderRadius: "8px",
          padding: "10px 24px",
          transition: "all 0.3s ease",
        }}
        onMouseEnter={(e) => {
          e.target.style.backgroundColor = COLORS.primary;
          e.target.style.color = COLORS.white;
        }}
        onMouseLeave={(e) => {
          e.target.style.backgroundColor = "transparent";
          e.target.style.color = COLORS.primary;
        }}
      >
        <i className="bi bi-x-lg me-2"></i>
        Effacer les filtres
      </button>
    </div>
  );

  // Composant pour l'affichage du tableau
  const renderIncidentTable = () => (
    <>
      <div className="table-responsive">
        <table className="table table-hover mb-0">
          <thead>
            <tr
              style={{
                backgroundColor: COLORS.lightBg,
                borderBottom: `2px solid ${COLORS.accent}`,
              }}
            >
              <th
                style={{
                  color: COLORS.primary,
                  fontWeight: "600",
                  padding: "16px",
                }}
              >
                Date création
              </th>
              <th
                style={{
                  color: COLORS.primary,
                  fontWeight: "600",
                  padding: "16px",
                }}
              >
                Agent
              </th>
              <th
                style={{
                  color: COLORS.primary,
                  fontWeight: "600",
                  padding: "16px",
                }}
              >
                Site
              </th>
              <th
                style={{
                  color: COLORS.primary,
                  fontWeight: "600",
                  padding: "16px",
                }}
              >
                Statut
              </th>
              <th
                style={{
                  color: COLORS.primary,
                  fontWeight: "600",
                  padding: "16px",
                }}
              >
                Contrôleur
              </th>
              <th
                style={{
                  color: COLORS.primary,
                  fontWeight: "600",
                  padding: "16px",
                  textAlign: "center",
                }}
              >
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {currentItems.map((incident, index) => (
              <IncidentRow
                key={incident.id_incident || `row-${index}`}
                incident={incident}
                index={index}
                updatingId={updatingId}
                onViewDetails={handleViewDetails}
                onOpenConfirmModal={openConfirmModal}
              />
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="d-flex justify-content-between align-items-center mt-4">
          <div className="d-flex align-items-center">
            <span className="me-2" style={{ color: COLORS.darkText }}>
              Afficher :
            </span>
            <select
              className="form-select form-select-sm w-auto"
              value={itemsPerPage}
              onChange={handleItemsPerPageChange}
              style={{
                borderColor: "#e0e0e0",
                color: COLORS.darkText,
                borderRadius: "6px",
              }}
            >
              <option value="5">5</option>
              <option value="10">10</option>
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>
            <span className="ms-3" style={{ color: COLORS.darkText }}>
              <span
                className="badge fw-semibold me-2"
                style={{
                  backgroundColor: "#E3F2FD",
                  color: COLORS.primary,
                  padding: "4px 12px",
                  borderRadius: "6px",
                }}
              >
                {filteredIncidents.length} incident(s)
                {filteredIncidents.length !== incidents.length
                  ? " (filtrés)"
                  : ""}
              </span>
            </span>
          </div>

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        </div>
      )}
    </>
  );

  // Composant pour la ligne d'incident
  const IncidentRow = ({
    incident,
    index,
    updatingId,
    onViewDetails,
    onOpenConfirmModal,
  }) => {
    const agentName = getAgentName(incident.agent);
    const siteName = getSiteName(incident.site);
    const controleurName = getControleurName(incident.controleur);
    const isResolved = incident.date_resolus_incident !== null;

    return (
      <tr style={{ borderBottom: `1px solid ${COLORS.lightBg}` }}>
        <td style={{ padding: "16px" }}>
          <div className="d-flex align-items-center">
            <div
              className="rounded-circle d-flex align-items-center justify-content-center me-3"
              style={{
                width: "36px",
                height: "36px",
                backgroundColor: "#E3F2FD",
                color: COLORS.primary,
                fontSize: "0.9rem",
              }}
            >
              <i className="bi bi-calendar"></i>
            </div>
            <div>
              <div style={{ color: COLORS.darkText, fontWeight: "500" }}>
                {formatDate(incident.date_creer_incident)}
              </div>
            </div>
          </div>
        </td>

        <td style={{ padding: "16px" }}>
          <div className="d-flex align-items-center">
            <div className="me-3">
              <div
                className="text-white rounded-circle d-flex align-items-center justify-content-center fw-semibold"
                style={{
                  width: "40px",
                  height: "40px",
                  fontSize: "14px",
                  backgroundColor: COLORS.primary,
                  boxShadow: "0 2px 4px rgba(11, 60, 93, 0.1)",
                }}
                title="Agent"
              >
                {agentName.charAt(0).toUpperCase()}
              </div>
            </div>
            <div>
              <div style={{ color: COLORS.darkText, fontWeight: "500" }}>
                {agentName}
              </div>
            </div>
          </div>
        </td>

        <td style={{ padding: "16px" }}>
          {incident.site ? (
            <div>
              <div style={{ color: COLORS.darkText, fontWeight: "500" }}>
                {siteName}
              </div>
              <small className="text-muted" style={{ fontSize: "0.8rem" }}>
                {incident.site.adresse}
              </small>
            </div>
          ) : (
            <span className="text-muted" style={{ fontSize: "0.85rem" }}>
              -
            </span>
          )}
        </td>

        <td style={{ padding: "16px" }}>
          <span
            className="badge fw-semibold"
            style={{
              backgroundColor: isResolved ? "#e8f5e9" : "#fff3e0",
              color: isResolved ? "#2e7d32" : "#f57c00",
              padding: "8px 16px",
              borderRadius: "6px",
              fontSize: "0.85rem",
            }}
          >
            {isResolved ? (
              <>
                <i className="bi bi-check-circle me-1"></i>
                Résolu
              </>
            ) : (
              <>
                <i className="bi bi-clock me-1"></i>
                Non résolu
              </>
            )}
          </span>
        </td>

        <td style={{ padding: "16px" }}>
          <div className="d-flex align-items-center">
            {controleurName !== "N/A" ? (
              <>
                <div className="me-3">
                  <div
                    className="text-white rounded-circle d-flex align-items-center justify-content-center fw-semibold"
                    style={{
                      width: "36px",
                      height: "36px",
                      fontSize: "13px",
                      backgroundColor: COLORS.secondary,
                      boxShadow: "0 2px 4px rgba(30, 136, 200, 0.1)",
                    }}
                    title="Contrôleur"
                  >
                    {controleurName.charAt(0).toUpperCase()}
                  </div>
                </div>
                <div>
                  <div style={{ color: COLORS.darkText, fontWeight: "500" }}>
                    {controleurName}
                  </div>
                </div>
              </>
            ) : (
              <span className="text-muted" style={{ fontSize: "0.85rem" }}>
                -
              </span>
            )}
          </div>
        </td>

        <td style={{ padding: "16px", textAlign: "center" }}>
          <div className="d-flex gap-2 justify-content-center">
            <button
              className="btn fw-semibold"
              onClick={() => onViewDetails(incident)}
              title="Voir détails"
              style={{
                color: COLORS.primary,
                borderColor: COLORS.primary,
                backgroundColor: "transparent",
                borderRadius: "6px",
                padding: "8px 16px",
                fontSize: "0.85rem",
                transition: "all 0.3s ease",
                minWidth: "44px",
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = COLORS.primary;
                e.target.style.color = COLORS.white;
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = "transparent";
                e.target.style.color = COLORS.primary;
              }}
            >
              <i className="bi bi-eye"></i>
            </button>

            {isResolved ? (
              <button
                className="btn fw-semibold"
                style={{
                  minWidth: "44px",
                  backgroundColor: COLORS.success,
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  padding: "8px 16px",
                  fontSize: "0.85rem",
                  cursor: "default",
                  opacity: 1,
                }}
                title="Incident déjà résolu"
                disabled
              >
                <i className="bi bi-check-circle"></i>
              </button>
            ) : (
              <button
                className="btn fw-semibold"
                onClick={() => onOpenConfirmModal(incident)}
                disabled={updatingId === incident.id_incident}
                title="Marquer comme résolu"
                style={{
                  color: COLORS.success,
                  borderColor: COLORS.success,
                  backgroundColor: "transparent",
                  borderRadius: "6px",
                  padding: "8px 16px",
                  fontSize: "0.85rem",
                  transition: "all 0.3s ease",
                  minWidth: "44px",
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = COLORS.success;
                  e.target.style.color = COLORS.white;
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = "transparent";
                  e.target.style.color = COLORS.success;
                }}
              >
                {updatingId === incident.id_incident ? (
                  <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                    aria-hidden="true"
                  ></span>
                ) : (
                  <i className="bi bi-check-circle"></i>
                )}
              </button>
            )}
          </div>
        </td>
      </tr>
    );
  };

  // Composant pour la pagination
  const Pagination = ({ currentPage, totalPages, onPageChange }) => {
    const [activeStyle, setActiveStyle] = useState({});

    const handlePageClick = (pageNumber) => {
      setActiveStyle({ [pageNumber]: true });
      setTimeout(() => {
        setActiveStyle({});
      }, 300);
      onPageChange(pageNumber);
    };

    return (
      <nav>
        <ul className="pagination pagination-sm mb-0">
          <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
            <button
              className="page-link fw-semibold"
              onClick={() => handlePageClick(currentPage - 1)}
              disabled={currentPage === 1}
              style={{
                color: currentPage === 1 ? "#B0BEC5" : COLORS.primary,
                borderColor: "#e0e0e0",
                padding: "6px 12px",
                borderRadius: "6px",
                margin: "0 2px",
              }}
            >
              <i className="bi bi-chevron-left"></i>
            </button>
          </li>

          {Array.from({ length: totalPages }, (_, i) => i + 1)
            .filter((page) => {
              if (totalPages <= 7) return true;
              return (
                page === 1 ||
                page === totalPages ||
                (page >= currentPage - 1 && page <= currentPage + 1)
              );
            })
            .map((page, index, array) => {
              if (index > 0 && page > array[index - 1] + 1) {
                return (
                  <React.Fragment key={`ellipsis-${page}`}>
                    <li className="page-item disabled">
                      <span
                        className="page-link"
                        style={{ borderColor: "#e0e0e0" }}
                      >
                        ...
                      </span>
                    </li>
                    <li
                      className={`page-item ${currentPage === page ? "active" : ""}`}
                    >
                      <button
                        className="page-link fw-semibold"
                        onClick={() => handlePageClick(page)}
                        style={{
                          backgroundColor:
                            currentPage === page || activeStyle[page]
                              ? COLORS.primary
                              : "transparent",
                          borderColor:
                            currentPage === page || activeStyle[page]
                              ? COLORS.primary
                              : "#e0e0e0",
                          color:
                            currentPage === page || activeStyle[page]
                              ? COLORS.white
                              : COLORS.primary,
                          padding: "6px 12px",
                          borderRadius: "6px",
                          margin: "0 2px",
                          transition: "all 0.3s ease",
                        }}
                      >
                        {page}
                      </button>
                    </li>
                  </React.Fragment>
                );
              }

              return (
                <li
                  key={page}
                  className={`page-item ${currentPage === page ? "active" : ""}`}
                >
                  <button
                    className="page-link fw-semibold"
                    onClick={() => handlePageClick(page)}
                    style={{
                      backgroundColor:
                        currentPage === page || activeStyle[page]
                          ? COLORS.primary
                          : "transparent",
                      borderColor:
                        currentPage === page || activeStyle[page]
                          ? COLORS.primary
                          : "#e0e0e0",
                      color:
                        currentPage === page || activeStyle[page]
                          ? COLORS.white
                          : COLORS.primary,
                      padding: "6px 12px",
                      borderRadius: "6px",
                      margin: "0 2px",
                      transition: "all 0.3s ease",
                    }}
                  >
                    {page}
                  </button>
                </li>
              );
            })}

          <li
            className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}
          >
            <button
              className="page-link fw-semibold"
              onClick={() => handlePageClick(currentPage + 1)}
              disabled={currentPage === totalPages}
              style={{
                color: currentPage === totalPages ? "#B0BEC5" : COLORS.primary,
                borderColor: "#e0e0e0",
                padding: "6px 12px",
                borderRadius: "6px",
                margin: "0 2px",
              }}
            >
              <i className="bi bi-chevron-right"></i>
            </button>
          </li>
        </ul>
      </nav>
    );
  };

  return (
    <Layout>
      <div className="pagetitle">
        <h1 style={{ color: COLORS.primary, fontWeight: "600" }}>
          Gestion des incidents
        </h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="/dashboard">Tableau de bord</a>
            </li>
            <li className="breadcrumb-item">Gestion</li>
            <li
              className="breadcrumb-item active"
              style={{ color: COLORS.secondary }}
            >
              Incidents
            </li>
          </ol>
        </nav>
      </div>

      <section className="section">
        <div
          className="card"
          style={{
            borderRadius: "12px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
            border: "none",
          }}
        >
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div>
                <h5
                  className="card-title mb-1"
                  style={{
                    color: COLORS.primary,
                    borderBottom: `2px solid ${COLORS.lightBg}`,
                    paddingBottom: "16px",
                    width: "100%",
                  }}
                >
                  <div className="d-flex align-items-center">
                    <div
                      className="rounded-circle d-inline-flex align-items-center justify-content-center me-3"
                      style={{
                        width: "36px",
                        height: "36px",
                        backgroundColor: "#ffebee",
                        color: COLORS.danger,
                      }}
                    >
                      <i className="bi bi-exclamation-triangle"></i>
                    </div>
                    Liste des incidents
                  </div>
                </h5>
              </div>

              <div className="d-flex gap-2">
                {hasActiveFilters() && (
                  <button
                    className="btn fw-semibold"
                    onClick={resetFilters}
                    title="Réinitialiser tous les filtres"
                    style={{
                      color: COLORS.primary,
                      borderColor: COLORS.primary,
                      backgroundColor: "transparent",
                      borderRadius: "8px",
                      padding: "10px 20px",
                      transition: "all 0.3s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = "#E3F2FD";
                      e.target.style.borderColor = COLORS.secondary;
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = "transparent";
                      e.target.style.borderColor = COLORS.primary;
                    }}
                  >
                    <i className="bi bi-arrow-clockwise me-2"></i>
                    Réinitialiser
                  </button>
                )}
              </div>
            </div>

            {/* Affichage de l'erreur d'abonnement */}
            {abonnementError && (
              <div className="alert alert-warning alert-dismissible fade show mb-4" style={{ 
                backgroundColor: '#fff3e0',
                borderColor: COLORS.warning,
                color: '#ff8f00',
                borderRadius: '8px',
                borderLeft: `4px solid ${COLORS.warning}`,
                whiteSpace: "pre-line"
              }}>
                <div className="d-flex align-items-start">
                  <i className="bi bi-exclamation-triangle me-2 mt-1"></i>
                  <div className="flex-grow-1">
                    {abonnementMessage}
                    <div className="d-flex gap-2 justify-content-center mt-3">
                      <a 
                        href="/abonnements/create"
                        className="btn fw-semibold"
                        style={{
                          backgroundColor: '#4caf50',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          padding: '8px 20px',
                          textDecoration: 'none',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#45a049'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = '#4caf50'}
                      >
                        <i className="bi bi-box-arrow-up-right"></i>
                        Souscrire à un abonnement
                      </a>
                      <button
                        className="btn fw-semibold"
                        onClick={() => {
                          setAbonnementError(false);
                          setAbonnementMessage("");
                        }}
                        style={{
                          backgroundColor: '#f44336',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          padding: '8px 20px'
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#d32f2f'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = '#f44336'}
                      >
                        Fermer
                      </button>
                    </div>
                  </div>
                  <button 
                    type="button" 
                    className="btn-close" 
                    onClick={() => {
                      setAbonnementError(false);
                      setAbonnementMessage("");
                    }}
                  ></button>
                </div>
              </div>
            )}

            {serverError && (
              <div
                className="alert alert-dismissible fade show mb-4"
                style={{
                  backgroundColor: "#ffebee",
                  borderColor: COLORS.danger,
                  color: "#c62828",
                  borderRadius: "8px",
                  borderLeft: `4px solid ${COLORS.danger}`,
                }}
              >
                <div className="d-flex align-items-center">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  <div className="flex-grow-1">{serverError}</div>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setServerError("")}
                  ></button>
                </div>
              </div>
            )}

            {infoMsg && !loading && (
              <div
                className="alert alert-success alert-dismissible fade show mb-4"
                style={{
                  backgroundColor: "#e8f5e9",
                  borderColor: COLORS.success,
                  color: "#2e7d32",
                  borderRadius: "8px",
                  borderLeft: `4px solid ${COLORS.success}`,
                }}
              >
                <div className="d-flex align-items-center">
                  <i className="bi bi-info-circle me-2"></i>
                  <div className="flex-grow-1">
                    {infoMsg.split("\n").map((line, i) => (
                      <div key={i}>{line}</div>
                    ))}
                  </div>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setInfoMsg("")}
                  ></button>
                </div>
              </div>
            )}

            {/* Filtres */}
            <FiltersSection
              filterSite={filterSite}
              setFilterSite={setFilterSite}
              filterStatut={filterStatut}
              setFilterStatut={setFilterStatut}
              filterControleur={filterControleur}
              setFilterControleur={setFilterControleur}
              filterDateDebut={filterDateDebut}
              setFilterDateDebut={setFilterDateDebut}
              filterDateFin={filterDateFin}
              setFilterDateFin={setFilterDateFin}
              sitesList={sitesList}
              controleursList={controleursList}
              incidents={incidents}
              filteredIncidents={filteredIncidents}
              hasActiveFilters={hasActiveFilters}
            />

            {loading ? (
              <div className="text-center py-5">
                <div
                  className="spinner-border"
                  role="status"
                  style={{ color: COLORS.primary }}
                >
                  <span className="visually-hidden">Chargement...</span>
                </div>
                <p className="mt-2" style={{ color: COLORS.darkText }}>
                  Chargement des incidents...
                </p>
              </div>
            ) : incidents.length === 0 && !abonnementError ? (
              <div className="text-center py-5">
                <div className="mb-4">
                  <div
                    className="rounded-circle d-flex align-items-center justify-content-center mx-auto"
                    style={{
                      width: "80px",
                      height: "80px",
                      backgroundColor: "#F5F7FA",
                      color: "#B0BEC5",
                    }}
                  >
                    <i className="bi bi-exclamation-triangle fs-1"></i>
                  </div>
                </div>
                <h6 className="mb-3" style={{ color: COLORS.primary }}>
                  Aucun incident trouvé
                </h6>
                <p
                  className="text-muted"
                  style={{
                    color: COLORS.darkText,
                    maxWidth: "500px",
                    margin: "0 auto",
                  }}
                >
                  Aucun incident n'a été enregistré pour le moment.
                </p>
                <button
                  onClick={fetchIncidents}
                  className="btn fw-semibold mt-3"
                  style={{
                    color: COLORS.primary,
                    borderColor: COLORS.primary,
                    backgroundColor: "transparent",
                    borderRadius: "8px",
                    padding: "10px 24px",
                    transition: "all 0.3s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = COLORS.primary;
                    e.target.style.color = COLORS.white;
                    e.target.style.transform = "translateY(-2px)";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = "transparent";
                    e.target.style.color = COLORS.primary;
                    e.target.style.transform = "translateY(0)";
                  }}
                >
                  <i className="bi bi-arrow-clockwise me-2"></i>
                  Actualiser
                </button>
              </div>
            ) : (
              <>
                {filteredIncidents.length === 0 && !abonnementError
                  ? renderNoResults()
                  : renderIncidentTable()}
              </>
            )}
          </div>
        </div>
      </section>

      {/* Modal de confirmation */}
      {showConfirmModal && selectedIncident && (
        <div
          className="modal fade show"
          style={{
            display: "block",
            backgroundColor: "rgba(0,0,0,0.5)",
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            zIndex: 1050,
          }}
        >
          <div
            className="modal-dialog modal-dialog-centered"
            style={{ maxWidth: "500px" }}
          >
            <div
              className="modal-content"
              style={{
                borderRadius: "12px",
                border: "none",
                boxShadow: "0 10px 40px rgba(0,0,0,0.2)",
              }}
            >
              <div
                className="modal-header"
                style={{
                  backgroundColor: COLORS.lightBg,
                  borderBottom: `2px solid ${COLORS.accent}`,
                  borderRadius: "12px 12px 0 0",
                  padding: "20px",
                }}
              >
                <h5
                  className="modal-title"
                  style={{ color: COLORS.primary, fontWeight: "600" }}
                >
                  <div className="d-flex align-items-center">
                    <div
                      className="rounded-circle d-inline-flex align-items-center justify-content-center me-2"
                      style={{
                        width: "30px",
                        height: "30px",
                        backgroundColor: COLORS.success,
                        color: "white",
                      }}
                    >
                      <i className="bi bi-check-circle"></i>
                    </div>
                    Confirmation
                  </div>
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closeConfirmModal}
                  disabled={updatingId === selectedIncident.id_incident}
                ></button>
              </div>

              <div className="modal-body" style={{ padding: "24px" }}>
                <p
                  className="mb-4"
                  style={{ color: COLORS.darkText, fontSize: "1rem" }}
                >
                  Êtes-vous sûr de vouloir marquer cet incident comme résolu ?
                </p>

                <div
                  className="p-4 rounded mb-4"
                  style={{
                    backgroundColor: "#F8F9FA",
                    borderLeft: `4px solid ${COLORS.primary}`,
                  }}
                >
                  <div className="row">
                    <div className="col-6 mb-3">
                      <small
                        className="text-muted d-block mb-1"
                        style={{ fontSize: "0.85rem" }}
                      >
                        <strong>Agent:</strong>
                      </small>
                      <div
                        style={{ color: COLORS.darkText, fontWeight: "500" }}
                      >
                        {getAgentName(selectedIncident.agent)}
                      </div>
                    </div>
                    <div className="col-6 mb-3">
                      <small
                        className="text-muted d-block mb-1"
                        style={{ fontSize: "0.85rem" }}
                      >
                        <strong>Site:</strong>
                      </small>
                      <div
                        style={{ color: COLORS.darkText, fontWeight: "500" }}
                      >
                        {getSiteName(selectedIncident.site)}
                      </div>
                    </div>
                  </div>
                </div>

                <label
                  className="form-label fw-semibold"
                  style={{ color: COLORS.darkText }}
                >
                  Actions effectuées pour résoudre l'incident{" "}
                  <span style={{ color: "#f44336" }}>*</span>
                </label>
                <textarea
                  className="form-control"
                  rows={4}
                  value={actionResolutionText}
                  onChange={(e) => setActionResolutionText(e.target.value)}
                  placeholder="Décrivez ce qui a été fait pour résoudre ce problème (agent remplacé, intervention technique, patrouille envoyée, etc.)"
                />
                {resolveFieldError && (
                  <div className="alert alert-danger py-2 mt-2 mb-0 small">
                    {resolveFieldError}
                  </div>
                )}
              </div>

              <div
                className="modal-footer"
                style={{
                  backgroundColor: COLORS.lightBg,
                  borderTop: `1px solid #dee2e6`,
                  borderRadius: "0 0 12px 12px",
                  padding: "20px",
                }}
              >
                <button
                  type="button"
                  className="btn fw-semibold"
                  onClick={closeConfirmModal}
                  disabled={updatingId === selectedIncident.id_incident}
                  style={{
                    color: COLORS.primary,
                    borderColor: COLORS.primary,
                    backgroundColor: "transparent",
                    borderRadius: "8px",
                    padding: "10px 24px",
                    transition: "all 0.3s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = "#E3F2FD";
                    e.target.style.borderColor = COLORS.secondary;
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = "transparent";
                    e.target.style.borderColor = COLORS.primary;
                  }}
                >
                  Annuler
                </button>
                <button
                  type="button"
                  className="btn fw-semibold"
                  onClick={handleMarkAsResolved}
                  disabled={updatingId === selectedIncident.id_incident}
                  style={{
                    color: COLORS.white,
                    backgroundColor: COLORS.success,
                    borderColor: COLORS.success,
                    borderRadius: "8px",
                    padding: "10px 24px",
                    transition: "all 0.3s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = "#43a047";
                    e.target.style.borderColor = "#43a047";
                    e.target.style.transform = "translateY(-2px)";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = COLORS.success;
                    e.target.style.borderColor = COLORS.success;
                    e.target.style.transform = "translateY(0)";
                  }}
                >
                  {updatingId === selectedIncident.id_incident ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      Traitement...
                    </>
                  ) : (
                    "Confirmer"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .pagetitle h1,
        .card-title,
        .btn,
        .page-link,
        th {
          font-family: 'Inter', sans-serif;
        }
        
        .card-body,
        .table,
        td,
        small,
        .alert {
          font-family: 'Roboto', sans-serif;
        }
        
        .btn:focus,
        .form-control:focus,
        .page-link:focus,
        .form-select:focus {
          outline: none;
          box-shadow: 0 0 0 0.2rem rgba(30, 136, 200, 0.25);
        }
        
        .btn,
        .badge,
        .page-link {
          transition: all 0.2s ease-in-out;
        }
        
        .btn:disabled,
        .page-link:disabled {
          cursor: not-allowed;
          opacity: 0.6;
        }
        
        .form-control:focus,
        .form-select:focus {
          border-color: ${COLORS.secondary};
          box-shadow: 0 0 0 0.2rem rgba(30, 136, 200, 0.25);
        }
        
        .table-hover tbody tr:hover {
          background-color: ${COLORS.lightBg};
          transition: background-color 0.2s ease;
        }
        
        .badge {
          font-weight: 500;
        }
        
        @media (max-width: 768px) {
          .card {
            margin: 0 -16px;
            border-radius: 0 !important;
          }
          
          .pagetitle h1 {
            font-size: 1.5rem;
          }
          
          .d-flex.justify-content-between {
            flex-direction: column;
            gap: 16px;
          }
          
          .d-flex.justify-content-between > div {
            width: 100%;
          }
          
          .btn {
            width: 100%;
            margin-bottom: 8px;
          }
          
          .table-responsive {
            border: none;
          }
          
          .table {
            font-size: 0.875rem;
          }
          
          .table th,
          .table td {
            padding: 12px 8px !important;
          }
          
          .row.g-3 {
            margin-bottom: 8px;
          }
          
          .row.g-3 > div {
            margin-bottom: 8px;
          }
          
          .modal-dialog {
            margin: 16px;
            width: calc(100% - 32px);
          }
        }
      `}</style>
    </Layout>
  );
};

// Composant pour les filtres
const FiltersSection = ({
  filterSite,
  setFilterSite,
  filterStatut,
  setFilterStatut,
  filterControleur,
  setFilterControleur,
  filterDateDebut,
  setFilterDateDebut,
  filterDateFin,
  setFilterDateFin,
  sitesList,
  controleursList,
  incidents,
  filteredIncidents,
  hasActiveFilters,
}) => {
  return (
    <div className="row mb-4">
      <div className="col-12">
        <div
          className="card"
          style={{
            backgroundColor: "#E3F2FD",
            border: "none",
            borderRadius: "8px",
          }}
        >
          <div className="card-body py-3">
            <div className="row g-3">
              {/* Filtre Site */}
              <div className="col-md-3">
                <div className="input-group">
                  <span
                    className="input-group-text"
                    style={{
                      backgroundColor: COLORS.lightBg,
                      borderColor: "#e0e0e0",
                      color: COLORS.secondary,
                    }}
                  >
                    <i className="bi bi-buildings"></i>
                  </span>
                  <select
                    className="form-select"
                    value={filterSite}
                    onChange={(e) => setFilterSite(e.target.value)}
                    style={{
                      borderColor: "#e0e0e0",
                      color: COLORS.darkText,
                    }}
                  >
                    <option value="">Tous les sites</option>
                    {sitesList.map((site) => (
                      <option key={site.id} value={site.id}>
                        {site.nom_complet}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Filtre Statut */}
              <div className="col-md-3">
                <div className="input-group">
                  <span
                    className="input-group-text"
                    style={{
                      backgroundColor: COLORS.lightBg,
                      borderColor: "#e0e0e0",
                      color: COLORS.secondary,
                    }}
                  >
                    <i className="bi bi-funnel"></i>
                  </span>
                  <select
                    className="form-select"
                    value={filterStatut}
                    onChange={(e) => setFilterStatut(e.target.value)}
                    style={{
                      borderColor: "#e0e0e0",
                      color: COLORS.darkText,
                    }}
                  >
                    <option value="">Tous les statuts</option>
                    <option value="non résolu">Non résolu</option>
                    <option value="résolu">Résolu</option>
                  </select>
                </div>
              </div>

              {/* Filtre Contrôleur */}
              <div className="col-md-3">
                <div className="input-group">
                  <span
                    className="input-group-text"
                    style={{
                      backgroundColor: COLORS.lightBg,
                      borderColor: "#e0e0e0",
                      color: COLORS.secondary,
                    }}
                  >
                    <i className="bi bi-person-check"></i>
                  </span>
                  <select
                    className="form-select"
                    value={filterControleur}
                    onChange={(e) => setFilterControleur(e.target.value)}
                    style={{
                      borderColor: "#e0e0e0",
                      color: COLORS.darkText,
                    }}
                  >
                    <option value="">Tous les contrôleurs</option>
                    {controleursList.map((controleur) => (
                      <option key={controleur.id} value={controleur.id}>
                        {controleur.nom_complet}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Filtre Date Début */}
              <div className="col-md-3">
                <div className="input-group">
                  <span
                    className="input-group-text"
                    style={{
                      backgroundColor: COLORS.lightBg,
                      borderColor: "#e0e0e0",
                      color: COLORS.secondary,
                    }}
                  >
                    <i className="bi bi-calendar-range"></i>
                  </span>
                  <input
                    type="date"
                    className="form-control"
                    value={filterDateDebut}
                    onChange={(e) => setFilterDateDebut(e.target.value)}
                    placeholder="Date début"
                    style={{
                      borderColor: "#e0e0e0",
                      color: COLORS.darkText,
                    }}
                  />
                </div>
              </div>

              {/* Filtre Date Fin */}
              <div className="col-md-3">
                <div className="input-group">
                  <span
                    className="input-group-text"
                    style={{
                      backgroundColor: COLORS.lightBg,
                      borderColor: "#e0e0e0",
                      color: COLORS.secondary,
                    }}
                  >
                    <i className="bi bi-calendar-range"></i>
                  </span>
                  <input
                    type="date"
                    className="form-control"
                    value={filterDateFin}
                    onChange={(e) => setFilterDateFin(e.target.value)}
                    placeholder="Date fin"
                    style={{
                      borderColor: "#e0e0e0",
                      color: COLORS.darkText,
                    }}
                    min={filterDateDebut}
                  />
                </div>
              </div>
            </div>

            {/* Résumé des filtres */}
            <div className="mt-3 d-flex justify-content-between align-items-center">
              <div
                className="text-muted small"
                style={{ color: COLORS.darkText }}
              >
                <i className="bi bi-info-circle me-1"></i>
                <span
                  className="badge fw-semibold me-2"
                  style={{
                    backgroundColor: COLORS.lightBg,
                    color: COLORS.primary,
                    padding: "4px 12px",
                    borderRadius: "6px",
                  }}
                >
                  {filteredIncidents.length} incident(s)
                  {filteredIncidents.length !== incidents.length &&
                    ` sur ${incidents.length} au total`}
                </span>

                {hasActiveFilters() && (
                  <span>
                    <i className="bi bi-funnel me-1"></i>
                    Filtres actifs:
                    {filterSite && (
                      <span
                        className="badge bg-info ms-1"
                        style={{ backgroundColor: COLORS.info }}
                      >
                        Site
                      </span>
                    )}
                    {filterStatut && (
                      <span
                        className="badge bg-info ms-1"
                        style={{ backgroundColor: COLORS.info }}
                      >
                        Statut
                      </span>
                    )}
                    {filterControleur && (
                      <span
                        className="badge bg-info ms-1"
                        style={{ backgroundColor: COLORS.info }}
                      >
                        Contrôleur
                      </span>
                    )}
                    {filterDateDebut && (
                      <span
                        className="badge bg-info ms-1"
                        style={{ backgroundColor: COLORS.info }}
                      >
                        Date début
                      </span>
                    )}
                    {filterDateFin && (
                      <span
                        className="badge bg-info ms-1"
                        style={{ backgroundColor: COLORS.info }}
                      >
                        Date fin
                      </span>
                    )}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IncidentList;